import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Checkbox, Modal } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Vendor } from '@totem/components/vendors/types';
import VendorContext from '@totem/components/vendors/VendorContext';
import VendorEditDialog from '@totem/components/vendors/VendorEditDialog';
import VendorDisplay from '@totem/components/vendors/vendorLogos/vendorDisplay';
import VendorLogoContext from '@totem/components/vendors/vendorLogos/vendorLogoContext';
import IBDIcon from '@totem/styles/Icon';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
const VendorTable = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { data, loading, setSelectedVendor, selectedVendors, onAction } =
    useContext(VendorContext);
  const { onAction: vendorLogoOnAction } = useContext(VendorLogoContext);
  const [isSending, setIsSending] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editVendor, setEditVendor] = useState<Vendor>(null);
  const getData = () => {
    if (typeof data !== 'undefined' && data !== null) {
      return data;
    }
    return [];
  };
  const handleFileDrop = async (
    event: React.DragEvent<HTMLDivElement>,
    vendor: Vendor,
  ) => {
    if (!isSending) {
      setIsSending(true);

      event.preventDefault();
      const file = event.dataTransfer.files[0];
      if (!file) {
        return;
      }
      const reader = new FileReader();
      try {
        reader.readAsDataURL(file);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to read file:', err);
      }
      reader.onload = () => {
        const base64DataUrl = reader.result as string;
        // OPTIONAL: Extract base64 only (strip "data:image/...;base64,")
        const [metadata, base64Image] = base64DataUrl.split(',');
        const mimeType = metadata.match(/data:(.*);base64/)?.[1] ?? 'image/*';
        Modal.confirm({
          title: `Upload logo for ${vendor.name}?`,
          content: (
            <img
              src={`data:${mimeType};base64,${base64Image}`}
              alt="Preview"
              width="100"
              style={{ marginTop: 10 }}
            />
          ),
          onOk: async () => {
            const formData = new FormData();
            formData.append('file', file);

            formData.append('vendor', vendor.name);
            const uri = `${DEVICES_ENDPOINT}/vendors/logo`;
            await fetch(uri, {
              method: 'PUT',
              headers: new Headers({
                Authorization: `Bearer ${getApiToken()}`,
              }),
              body: formData,
            }).then((res) => {
              setIsSending(false);

              CheckResponseShowError(res);

              onAction('refresh', vendor);

              vendorLogoOnAction('refresh', vendor);
            });
          },
        });
      };
    }
  };
  const handleCheckToggle = (vendor: Vendor, isSelected: boolean) => {
    setSelectedVendor(vendor, isSelected);
  };
  const columns: ColumnProps<Vendor>[] = [
    {
      render: (_, record: Vendor) => (
        <>
          {permissions.internalSystemAdministrator.hasPermissions(
            userProfile,
          ) && (
            <IBTooltip
              content={permissions.internalSystemAdministrator.tooltip}
            >
              <div
                onDrop={(event) => handleFileDrop(event, record)}
                onDragOver={(evt) => evt.preventDefault()}
                style={{
                  border: '2px dashed #ccc',
                  padding: '8px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: '#fafafa',
                }}
              >
                <Checkbox
                  checked={
                    selectedVendors.findIndex(
                      (chk) => chk.name === record.name,
                    ) >= 0
                  }
                  onChange={(evt) =>
                    handleCheckToggle(record, evt.target.checked)
                  }
                />
              </div>
            </IBTooltip>
          )}
        </>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record: Vendor) => (
        <Link to={`/dashboard/vendors/${encodeURIComponent(record.name)}`}>
          <VendorDisplay vendorName={record.name} />
        </Link>
      ),
      defaultSortOrder: 'ascend',
      sorter: (compA, compB) =>
        compA.name === compB.name ? 0 : compA.name > compB.name ? 1 : -1,
    },
    {
      title: 'Aliases',
      dataIndex: 'aliases',
      key: 'aliases',
      render: (_, record: Vendor) => (
        <span>
          {record.aliases !== null &&
            record.aliases.map((alias, idx) =>
              idx === 0 ? (
                <span key={alias}>{alias}</span>
              ) : (
                <span key={alias}>
                  <br />
                  {alias}
                </span>
              ),
            )}
        </span>
      ),
    },
    {
      title: 'Actions',
      width: 30,
      dataIndex: 'additionalOptions',
      key: 'additionalOptions',
      render: (_, record: Vendor) => (
        <div className="center-table-cell">
          {permissions.systemAdministrator.hasPermissions(userProfile) && (
            <IBTooltip
              content={permissions.internalSystemAdministrator.tooltip}
            >
              <IBDIcon
                name={'Edit'}
                onClick={() => {
                  setEditVendor(record);

                  setShowEditDialog(true);
                }}
              />
            </IBTooltip>
          )}
        </div>
      ),
    },
  ];
  return (
    <>
      <IBTable
        showSorterTooltip
        columns={columns}
        dataSource={getData()}
        loading={loading}
        rowKey={(record) => record.name}
        pagination={{
          total: typeof data !== 'undefined' && data !== null ? data.length : 0,
          showSizeChanger: true,
        }}
      />
      {showEditDialog && editVendor !== null && (
        <VendorEditDialog
          vendorName={editVendor.name}
          onClose={() => {
            setShowEditDialog(false);
          }}
          open={showEditDialog}
        />
      )}
    </>
  );
};
export default VendorTable;
